import React, { useContext } from 'react';
import {
  Box,
  Flex,
  Text,
  Tooltip,
  Button,
  ButtonGroup,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from 'styled-components';
import { FlowsQuestion } from '@flows/src/types/FlowsTypes';
import useFlowInputComponent from '@flows/src/hooks/useFlowsInputComponent';
import FlowsQuestionText from '../../../ui/advisory-flow/components/FlowsQuestionText';
import TooltipCircle from '../../../../../../common/src/components/TooltipCircle';
import { handleResponseLanguage } from '../../../../../../../i18n';
import { useFlowsSelector } from '../../../../../../common/src/utils/globalVariables';
import { parseStringForAnswers } from '../../../../utils/stringUtils';

interface Props {
  question: FlowsQuestion;
}

const RadioYesNoSmallInputComponent: React.FC<
  React.PropsWithChildren<Props>
> = ({ question }) => {
  const { t } = useTranslation();

  const {
    field: { onBlur, value: isSelected },
    error,
  } = useFlowInputComponent({ question });

  // Prepare parsed description for tooltip and truncated text
  const answers = useFlowsSelector((state) => state.flows.answers);
  const translatedDescription = handleResponseLanguage(question.description);
  const parsedDescription = parseStringForAnswers({
    answers,
    question: translatedDescription,
    bold: true,
    index: question.index,
  });

  const theme = useContext(ThemeContext);

  // --- segment click handler ---
  const handleSegmentClick = (value: boolean) => {
    if (isSelected === value) {
      if (!question.rules.mandatory) {
        onBlur({ name: question.id, value: null });
      }
      return;
    }
    onBlur({ name: question.id, value });
  };

  const borderColor = error?.message ? theme.status.error : 'gray.50';

  return (
    <Flex
      bg="white"
      w="auto"
      maxW="100%"
      p="0.5"
      pl="8"
      pr="3"
      minH="6.4rem"
      border="1px"
      borderColor={borderColor}
      borderRadius="6px"
      transitionDuration="0.2s"
      direction="row"
      align="center"
      boxShadow="base"
      transition="0.2s"
      {...(!question.rules.is_disabled && {
        _hover: { borderColor: 'gray.200' },
      })}
    >
      <Box flexGrow={1} mr="8">
        <Flex align="flex-start" gap="1">
          <FlowsQuestionText whiteSpace="pre-wrap" question={question} />
          {question.description && (
            <Tooltip
              label={
                <Text
                  whiteSpace="pre-wrap"
                  dangerouslySetInnerHTML={{ __html: parsedDescription }}
                />
              }
              shouldWrapChildren
            >
              <TooltipCircle size="sm" mt="0.5" flexShrink={0}>
                ?
              </TooltipCircle>
            </Tooltip>
          )}
        </Flex>
      </Box>
      <ButtonGroup isAttached alignSelf="center" size="sm" variant="outline">
        <Button
          onClick={() => handleSegmentClick(false)}
          isActive={isSelected === false}
          isDisabled={question.rules.is_disabled}
          borderRightRadius="0"
          data-test-id={`FLOW_INPUT-TYPE_SEGMENT_SMALL_${question.id
            .toUpperCase()
            .replaceAll('.', '-')}_BUTTON-NO`}
        >
          {t('No')}
        </Button>
        <Button
          onClick={() => handleSegmentClick(true)}
          isActive={isSelected === true}
          isDisabled={question.rules.is_disabled}
          borderLeftRadius="0"
          data-test-id={`FLOW_INPUT-TYPE_SEGMENT_SMALL_${question.id
            .toUpperCase()
            .replaceAll('.', '-')}_BUTTON-YES`}
        >
          {t('Yes')}
        </Button>
      </ButtonGroup>
    </Flex>
  );
};

export default RadioYesNoSmallInputComponent;
